import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Trash2, Upload, Plus, Lock, X } from 'lucide-react';
import { Skill, Project } from '../../types';
import { LANGUAGES } from '../../utils';
import { BACKEND_URL } from '../../config';
import { FormSection } from '../ui';
import FormShell from './FormShell';
import SkillManagerModal from '../SkillManagerModal';
import SkillPicker from './SkillPicker';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormationFormData {
  name: string;
  localisation: string;
  isStudy: boolean;
  startDate: string;
  endDate: string;
  isOngoing: boolean;
  type: Record<string, string>;
  degree: Record<string, string>;
  position: Record<string, string>;
  courses: Record<string, string>;
  tasks: Record<string, string>;
  manualSkillIds: number[];
  selectedProjectIds: number[];
  privateLinks: { title: Record<string, string>; url: string }[];
}

interface FormationFormProps {
  mode: 'new' | 'edit';
  isStudy: boolean;
  initialData?: Partial<FormationFormData> & {
    existingLogoUrl?: string | null;
    manualSkills?: Skill[];
  };
  onSubmit: (data: FormationFormData & { logoFile: File | null }) => Promise<void>;
  submitting: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function FormationForm({ mode, isStudy, initialData, onSubmit, submitting }: FormationFormProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // ── Core state ──────────────────────────────────────────────────────────────
  const [name, setName] = useState(initialData?.name ?? '');
  const [localisation, setLocalisation] = useState(initialData?.localisation ?? '');
  const [startDate, setStartDate] = useState(initialData?.startDate ?? '');
  const [endDate, setEndDate] = useState(initialData?.endDate ?? '');
  const [isOngoing, setIsOngoing] = useState(initialData?.isOngoing ?? false);
  const [type, setType] = useState<Record<string, string>>(initialData?.type ?? { fr: '', en: '' });
  const [degree, setDegree] = useState<Record<string, string>>(initialData?.degree ?? { fr: '', en: '' });
  const [position, setPosition] = useState<Record<string, string>>(initialData?.position ?? { fr: '', en: '' });
  const [courses, setCourses] = useState<Record<string, string>>(initialData?.courses ?? { fr: '', en: '' });
  const [tasks, setTasks] = useState<Record<string, string>>(initialData?.tasks ?? { fr: '', en: '' });
  const [privateLinks, setPrivateLinks] = useState<{ title: Record<string, string>; url: string }[]>(
    initialData?.privateLinks ?? []
  );

  // ── Skills state ────────────────────────────────────────────────────────────
  const [manualSkills, setManualSkills] = useState<Skill[]>(initialData?.manualSkills ?? []);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);

  // ── Projects state ──────────────────────────────────────────────────────────
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>(initialData?.selectedProjectIds ?? []);

  // ── Media state ─────────────────────────────────────────────────────────────
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(initialData?.existingLogoUrl ?? null);

  const [textLang, setTextLang] = useState(lang);

  // ── Modals ──────────────────────────────────────────────────────────────────
  const [skillModalOpen, setSkillModalOpen] = useState(false);

  // ── CSS helpers ─────────────────────────────────────────────────────────────
  const inputCls = "glass rounded-lg w-full border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition text-sm";
  const selectCls = "glass rounded-lg border border-ink/[0.08] text-zinc-200 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition text-sm";
  const labelCls = "block text-xs font-medium text-zinc-400 mb-1.5";

  // ── Fetch skills + projects ─────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/skills`).then(r => r.json()).then(setAllSkills).catch(console.error);
    fetch(`${BACKEND_URL}/api/projects`).then(r => r.json()).then(setAllProjects).catch(console.error);
  }, []);


  // ── Handlers ────────────────────────────────────────────────────────────────
  const toggleSkill = (skill: Skill) =>
    setManualSkills(prev => (prev.some(e => e.id === skill.id) ? prev.filter(e => e.id !== skill.id) : [...prev, skill]));

  const addProject = (id: number) => {
    if (!selectedProjectIds.includes(id)) setSelectedProjectIds(prev => [...prev, id]);
  };

  const removeProject = (id: number) => {
    setSelectedProjectIds(prev => prev.filter(pid => pid !== id));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); }
  };

  const handleLogoDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const f = Array.from(e.dataTransfer.files).find(f => f.type.startsWith('image/'));
    if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      name,
      localisation,
      isStudy,
      startDate,
      endDate: isOngoing ? '' : endDate,
      isOngoing,
      type,
      degree,
      position,
      courses,
      tasks,
      manualSkillIds: manualSkills.map(s => s.id),
      selectedProjectIds,
      privateLinks: privateLinks.filter(l => l.url.trim()),
      logoFile,
    });
  };

  const checkCls = 'inline-flex items-center gap-2 min-h-[44px] text-sm text-ink cursor-pointer';
  const richText = isStudy ? courses : tasks;
  const setRichText = isStudy ? setCourses : setTasks;

  const sections: [string, string][] = [
    ['ff-identity', String(t('identity'))],
    ['ff-content', String(isStudy ? t('courses') : t('tasks'))],
    ['ff-skills', String(t('skills_skills'))],
    ['ff-projects', String(t('projects'))],
    ['ff-logo', 'Logo'],
    ['ff-private', String(t('private_links'))],
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <FormShell
        onSubmit={handleFormSubmit}
        submitting={submitting}
        submitLabel={String(mode === 'new' ? t('create_formation') : t('save'))}
        sections={sections}
      >
        <FormSection id="ff-identity" title={String(t('identity'))}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className={labelCls}>{isStudy ? t('school') : t('company')} *</span>
              <input className={inputCls} value={name} onChange={e => setName(e.target.value)} required />
            </label>
            <label className="block">
              <span className={labelCls}>{t('location')} *</span>
              <input className={inputCls} value={localisation} onChange={e => setLocalisation(e.target.value)} required />
            </label>
            <label className="block">
              <span className={labelCls}>{isStudy ? t('school_type') : t('contract_type')} *</span>
              <input className={inputCls} value={type[lang] || ''} onChange={e => setType(prev => ({ ...prev, [lang]: e.target.value }))} required />
            </label>
            {isStudy ? (
              <label className="block">
                <span className={labelCls}>{t('degree')}</span>
                <input className={inputCls} value={degree[lang] || ''} onChange={e => setDegree(prev => ({ ...prev, [lang]: e.target.value }))} />
              </label>
            ) : (
              <label className="block">
                <span className={labelCls}>{t('position')} *</span>
                <input className={inputCls} value={position[lang] || ''} onChange={e => setPosition(prev => ({ ...prev, [lang]: e.target.value }))} required />
              </label>
            )}
            <label className="block">
              <span className={labelCls}>{t('start_date')} *</span>
              <input type="date" className={`w-full ${selectCls}`} value={startDate} onChange={e => setStartDate(e.target.value)} required />
            </label>
            <label className="block">
              <span className={labelCls}>{t('end_date')} {!isOngoing && '*'}</span>
              <input type="date" className={`w-full ${selectCls} disabled:opacity-40`} value={endDate} disabled={isOngoing} required={!isOngoing}
                onChange={e => setEndDate(e.target.value)} />
            </label>
          </div>
          <label className={checkCls}>
            <input type="checkbox" className="w-4 h-4 accent-primary" checked={isOngoing}
              onChange={e => setIsOngoing(e.target.checked)} />
            {t('on_going')}
          </label>
        </FormSection>

        <FormSection id="ff-content" title={String(isStudy ? t('courses') : t('tasks'))}>
          <div className="flex gap-2" role="group" aria-label={t('language') as string}>
            {LANGUAGES.map(({ code }) => (
              <button key={code} type="button" onClick={() => setTextLang(code)} aria-pressed={textLang === code}
                className={`h-8 px-3 rounded-full text-xs font-semibold transition-colors ${textLang === code ? 'bg-primary text-white' : 'bg-surface text-muted hover:text-ink'}`}>
                {code.toUpperCase()}
              </button>
            ))}
          </div>
          {LANGUAGES.map(({ code }) => (
            <div key={`${code}-${mode}`} className={code !== textLang ? 'hidden' : ''}>
              <ReactQuill value={richText[code] || ''} onChange={v => setRichText(prev => ({ ...prev, [code]: v }))}
                placeholder={(isStudy ? t('courses') : t('tasks')) as string} />
            </div>
          ))}
        </FormSection>

        <FormSection id="ff-skills" title={String(t('skills_skills'))} count={manualSkills.length}>
          <SkillPicker skills={allSkills} selected={manualSkills} onToggle={toggleSkill} onManage={() => setSkillModalOpen(true)} />

        </FormSection>

        <FormSection id="ff-projects" title={String(t('projects'))} count={selectedProjectIds.length}>
          <select className={`w-full ${selectCls}`} value="" onChange={e => addProject(+e.target.value)} aria-label={t('formation_select_project') as string}>
            <option value="" disabled>{t('formation_select_project')}</option>
            {allProjects
              .filter(p => !selectedProjectIds.includes(p.id))
              .sort((a, b) => a.title.localeCompare(b.title, lang))
              .map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          {selectedProjectIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {allProjects
                .filter(p => selectedProjectIds.includes(p.id))
                .sort((a, b) => a.title.localeCompare(b.title, lang))
                .map(p => (
                  <span key={p.id} className="flex items-center gap-1.5 bg-surface border border-line px-3 py-1.5 rounded-lg text-sm text-zinc-300">
                    {p.title}
                    <button type="button" onClick={() => removeProject(p.id)} className="text-red-400/60 hover:text-red-400 transition-colors" aria-label={t('delete') as string}>
                      <X size={13} />
                    </button>
                  </span>
                ))}
            </div>
          )}
        </FormSection>

        <FormSection id="ff-logo" title="Logo">
          <div className="relative max-w-xs">
            <label
              className="flex h-40 items-center justify-center rounded-lg border-2 border-dashed border-ink/[0.08] bg-surface text-zinc-500 hover:border-primary/40 hover:text-primary-light cursor-pointer transition-all overflow-hidden"
              onDragOver={e => e.preventDefault()}
              onDrop={handleLogoDrop}
            >
              {logoPreview ? <img src={logoPreview} alt="" className="w-full h-full object-contain" /> : (
                <span className="flex flex-col items-center gap-1.5 text-sm"><Upload size={18} /> 300×300</span>
              )}
              <input type="file" accept="image/*" className="hidden" aria-label="Logo" onChange={handleLogoChange} />
            </label>
            {logoPreview && (
              <button type="button" onClick={() => { setLogoFile(null); setLogoPreview(null); }} aria-label={t('delete') as string}
                className="absolute top-2 right-2 bg-red-600/90 hover:bg-red-500 text-white p-1.5 rounded-lg transition-colors">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </FormSection>

        <FormSection id="ff-private" title={String(t('private_links'))}>
          <p className="text-xs text-zinc-500">{t('private_links_description')}</p>
          {privateLinks.map((link, idx) => (
            <div key={idx} className="flex gap-2">
              <input className={`${inputCls} !w-1/3`} placeholder={`${t('link_title')} (${lang.toUpperCase()})`} value={link.title[lang] || ''}
                onChange={e => setPrivateLinks(privateLinks.map((l, i) => (i === idx ? { ...l, title: { ...l.title, [lang]: e.target.value } } : l)))} />
              <div className="relative flex-1">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-500/50 pointer-events-none" />
                <input className={`${inputCls} !pl-9`} placeholder={t('link_url') as string} value={link.url}
                  onChange={e => setPrivateLinks(privateLinks.map((l, i) => (i === idx ? { ...l, url: e.target.value } : l)))} />
              </div>
              <button type="button" onClick={() => setPrivateLinks(privateLinks.filter((_, i) => i !== idx))} aria-label={t('delete') as string}
                className="p-2.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-all">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => setPrivateLinks(prev => [...prev, { title: { fr: '', en: '' }, url: '' }])}
            className="inline-flex items-center gap-1.5 min-h-[36px] text-xs text-amber-500 font-medium">
            <Plus size={14} /> {t('add_private_link')}
          </button>
        </FormSection>
      </FormShell>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      <SkillManagerModal
        open={skillModalOpen}
        onClose={() => setSkillModalOpen(false)}
        onCreated={skill => setAllSkills(prev => [...prev, skill])}
        onDeleted={id => {
          setAllSkills(prev => prev.filter(s => s.id !== id));
          setManualSkills(prev => prev.filter(s => s.id !== id));
        }}
      />
    </>
  );
}
